import { defineStore } from "pinia";

import api from "@/api";
import { useOrganizationStore } from "./organization";

const hasArtifactPermission = (state, permission) =>
  state.perms.some(({ code }) => code === "view-admin-page") ||
  state.artifactCapabilities?.[permission] === true;

/**
 * store for the users
 * - user is the user's info of the logged in users
 * - perms is the permissions of the logged in user
 * - hq_users is a list of users who can be part of a department
 * - loading is a flag to indicate if the store is currently loading data
 * - all is a list of a limited fields of all users:
 *      id, username, organization_id, first_name, last_name, email, is_hq
 *
 */

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null,
    perms: [],
    loading: false,
    allDict: {},
    multiOrgAllDict: {}, // Make this a new state so it doesn't effect current state
    canSeeAllOrgs: false,
    canSeeAdmin: false,
    artifactCapabilities: {},
    dialectCapability: null,
    canManageTrainingAccess: false,
    allowedOrganizations: [],
    multiOrgUser: false,
    activeOrganization: null,
    settings: [],
  }),
  getters: {
    activeUsers: (state) => {
      return Object.values(state.allDict).filter((user) => user._is_active);
    },
    activeUsersDict: (state) => {
      return state.activeUsers.reduce((dict, user) => {
        dict[user.id] = user;
        return dict;
      }, {});
    },
    allSupportableUsers: (state) => {
      return Object.values(state.allDict).filter(
        (user) => user.status !== "INCOMPLETE"
      );
    },
    hqUsers: (state) =>
      Object.values(state.allDict).filter((user) => user.is_hq),
    activeHqUsers: (state) => {
      return state.hqUsers.filter((user) => user._is_active);
    },
    activeHqUsersDict: (state) => {
      return state.activeHqUsers.reduce((dict, user) => {
        dict[user.id] = user;
        return dict;
      }, {});
    },
    hqUsersDict: (state) => {
      return state.hqUsers.reduce((dict, user) => {
        dict[user.id] = user;
        return dict;
      }, {});
    },
    authUserId: (state) => {
      return state.user?.id;
    },
    authUser: (state) => {
      return state.user;
    },
    authUserEmail: (state) => {
      return state.user?.email;
    },
    authUserOrganizationId: (state) => {
      return state.activeOrganization || state.user?.organization_id;
    },
    allUsersDropdownOptions: (state) => {
      return Object.values(state.allDict).map((user) => {
        return {
          avatar: user.shortName,
          text: user.name,
          value: user.id,
        };
      });
    },
    hqUsersDropdownOptions: (state) => {
      return state.hqUsers.map((user) => {
        return {
          avatar: user.shortName,
          text: user.name,
          value: user.id,
        };
      });
    },
    hqActiveUsersDropdownOptions: (state) => {
      return state.activeHqUsers.map((user) => {
        return {
          avatar: user.shortName,
          text: user.name,
          value: user.id,
        };
      });
    },
    hqUsersBasicDropdownOptions() {
      return this.activeHqUsers.map(({ id, name }) => {
        return {
          id,
          name,
        };
      });
    },
    allActiveUsersDropdownOptions: (state) => {
      return state.activeUsers.map((user) => {
        return {
          avatar: user.shortName,
          text: user.name,
          value: user.id,
        };
      });
    },
    allSupportableUsersWithEmailDropdownOptions: (state) => {
      return state.allSupportableUsers.map((user) => {
        return {
          avatar: user.shortName,
          text: `${user.name} (${user.email})`,
          value: user.id,
        };
      });
    },
    permCodes: (state) => {
      return state.perms.map((permObj) => permObj.code);
    },
    /** Allow dialect navigation for viewing or management access. */
    canAccessDialects() {
      return ["view_only", "personal_only", "global_and_personal"].includes(this.dialectCapability);
    },
    /** Creation needs personal or global capability as well as dialect access. */
    canCreateDialects() {
      return this.canAccessDialects &&
        ["personal_only", "global_and_personal"].includes(this.dialectCapability);
    },
    isDeveloper: (state) =>
      state.user?.role_codes?.some((code) => code.toLowerCase() === "developer") ?? false,
    hasArtifactAccess: (state) => (view) => hasArtifactPermission(state, view),
    myPhoneNumbers: (state) => {
      return state.user.phone_numbers || [];
    },
    myEmailAddresses: (state) => {
      return state.user.email_addresses || [];
    },
  },
  actions: {
    /** Cache dialect access until the selected organization changes. */
    async fetchDialectCapability(force = false) {
      const organizationId = this.authUserOrganizationId;
      if (!organizationId) return;
      if (!force && this.dialectCapability !== null) return;

      this.dialectCapability = null;
      this.canManageTrainingAccess = false;
      try {
        const response = await api.dialects.fetchDialectCapability(organizationId);
        if (this.authUserOrganizationId === organizationId) {
          this.dialectCapability = response.capability;
          this.canManageTrainingAccess = response.can_manage_training_access;
        }
      } catch (error) {
        console.error("Could not load dialect capability:", error);
      }
    },

    async fetchMyProfile(loading = true) {
      try {
        if (loading) {
          this.loading = true;
        }
        // Call the backend endpoint to check authentication status
        const profile = await api.user.fetchMyProfile();
        this.user = profile.user;
        this.perms = profile.perms;
        this.allowedOrganizations = profile.allowed_organizations;
        this.settings = profile.settings;
        this.artifactCapabilities = profile.artifact_capabilities;

        // If this.activeOrganization is null, check if there's a saved organization in localStorage
        if (this.activeOrganization === null) {
          const savedOrg = this.user?.id
            ? localStorage.getItem(`activeOrg-${this.user.id}`)
            : null;

          if (savedOrg && this.allowedOrganizations.includes(savedOrg)) {
            this.setActiveOrganization(savedOrg);
          } else {
            this.setActiveOrganization(this.user?.organization_id ?? null);
          }
        }

        await this.fetchDialectCapability();

        if (this.allowedOrganizations.length > 1) {
          this.multiOrgUser = true;
        }

        // caching important permissions
        for (let perm of this.perms) {
          if (perm.code === "get_all_organization") {
            this.canSeeAllOrgs = true;
          }
          if (perm.code === "get_admin_info") {
            this.canSeeAdmin = true;
          }
        }

        console.debug("perms: ", toRaw(this.perms));
        return true;
      } catch (error) {
        console.error(error.message);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async fetchAllUsers(shouldReload = true) {
      try {
        if (shouldReload) {
          this.loading = true;
        }
        // Call the backend endpoint to check authentication status
        const organizationId = this.activeOrganization ?? null;
        const users = await api.user.fetchUserList(null, organizationId);

        const orgs = useOrganizationStore();
        const is_proco_org = orgs.myOrganizationIsProco;

        let filteredUsers = users;

        if (!is_proco_org) {
          filteredUsers = filteredUsers.filter((user) => !user.is_superuser);
        }

        this.allDict = filteredUsers.reduce((dict, user) => {
          dict[user.id] = user;
          return dict;
        }, {});
        // console.log("this.allDict: ", toRaw(this.allDict));
      } catch (error) {
        console.error(error.message);
      } finally {
        if (shouldReload) {
          this.loading = false;
        }
      }
    },

    hasPerm(permName) {
      return this.perms.map(({ code }) => code).includes(permName);
    },

    /** Select an organization and clear its cached dialect access. */
    setActiveOrganization(organizationId) {
      this.activeOrganization = organizationId;
      this.dialectCapability = null;
      this.canManageTrainingAccess = false;

      if (this.user?.id) {
        localStorage.setItem(`activeOrg-${this.user.id}`, organizationId);
      }
    },
    async insertToMultiOrgAllDict(orgId) {
      try {
        // this.loading = true;


        if(!this.allowedOrganizations.includes(orgId)) {
          throw new Error(`Cannot get users, no permissions to access this org: ${orgId}`)
        }
    
        const users = await api.user.fetchUserList(null, orgId)

        const orgs = useOrganizationStore();
        const is_proco_org = orgs.myOrganizationIsProco;

        let filteredUsers = users

        if (!is_proco_org) {
          filteredUsers = filteredUsers.filter((user) => !user.is_superuser);
        }

        filteredUsers.forEach(user => {
          this.multiOrgAllDict[user.id] = user
        })
      } catch (err) {
        console.error(err.message);
      } finally {
        // this.loading = false;
      }
    },
    removeUnitFromUsersByUnitIds(unitIds) {
      const unitIdSet = new Set(unitIds);
      Object.values(this.allDict).forEach((user) => {
        if (unitIdSet.has(user.unit_id)) {
          user.unit_id = null;
        }
      });
    },

    clearMultiOrgAllDict() {
      this.multiOrgAllDict = {}
    }
  },
});
